"use client";

import React from "react";
import { Wrench, PhoneCall, ShieldCheck, Clock, CircleCheck as CheckCircle2, Sparkles, MapPin, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
const keyPoints = ["Genuine Kohler OEM replacement parts", "Class Five, AquaPiston & Cimarron experts", "Fast same-day & 24/7 emergency service in Jonesboro"];
const highlights = [{
  icon: ShieldCheck,
  title: "Certified Kohler Specialists",
  desc: "Experienced with canister flappers, tower valves, and dual-flush mechanisms."
}, {
  icon: Clock,
  title: "24/7 Rapid Response",
  desc: "Plumbers ready for urgent leaks, continuous running, or severe clogs."
}, {
  icon: MapPin,
  title: "Serving Jonesboro, GA",
  desc: "Local licensed team equipped for immediate on-site Kohler repairs."
}];
export default function KohlerHero() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="kohlertoiletrepairhero" data-nav="light" className="relative overflow-hidden bg-background text-foreground pt-12 pb-16 md:pt-20 md:pb-24 border-b border-border">
      {/* Subtle decorative background glow */}
      <div className="absolute top-0 right-0 -z-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -z-10 w-80 h-80 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Headline and CTAs */}
          <div className="lg:col-span-7 flex flex-col justify-center text-left">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge variant="secondary" className="px-3 py-1 font-semibold text-secondary-foreground gap-1.5 rounded-full">
                <Sparkles className="w-3.5 h-3.5 text-accent" />
                Kohler Fixture Specialists
              </Badge>
              <Badge variant="outline" className="px-3 py-1 font-medium text-muted-foreground rounded-full">
                Jonesboro & Surrounding Clayton County
              </Badge>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight mb-5">
              Expert Kohler Toilet Repair & Service in Jonesboro
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6 max-w-2xl">
              Restore strong flushes, stop ghost leaks, and solve stubborn fill valve problems. Our licensed Jonesboro plumbers use authentic Kohler factory components to keep your luxury fixtures performing quietly and efficiently.
            </p>

            {/* Checklist */}
            <div className="grid grid-cols-1 gap-2.5 mb-8">
              {keyPoints.map((point, index) => <div data-index={index} key={index} className="flex items-center gap-2.5 text-foreground">
                  <CheckCircle2 className="w-5 h-5 text-accent shrink-0" />
                  <span className="text-sm sm:text-base font-medium">{point}</span>
                </div>)}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-8">
              <Button asChild size="lg" className="rounded-xl px-7 text-base font-semibold shadow-md">
                <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="w-5 h-5" />
                  Request Emergency Service
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-xl px-6 text-base font-medium">
                <Link to="/service-areas/" className="inline-flex items-center justify-center gap-2">
                  View Service Areas
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>

            {/* Contact quick strip */}
            <div className="flex items-center gap-3 pt-4 border-t border-border">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Wrench className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  Need Immediate Help?
                </p>
                <p className="text-sm font-semibold text-foreground">
                  Jonesboro Emergency Plumber on call 24 Hours a Day
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Imagery & Highlight Cards */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-lg">
              <AspectRatio ratio={4 / 3}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/kohler_toilet_repair_service.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/kohler_toilet_repair_service.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/kohler_toilet_repair_service.png-wvc-sizes" alt="Modern Kohler toilet and plumbing fixtures repair in bathroom" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </AspectRatio>

              {/* Floating feature badge */}
              <motion.div initial={shouldReduceMotion ? {
              opacity: 1,
              y: 0
            } : {
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="absolute bottom-4 left-4 right-4 bg-background/95 backdrop-blur-md p-4 rounded-xl border border-border shadow-md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center text-accent-foreground font-bold text-lg">
                      K
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">Kohler Genuine Parts</p>
                      <p className="text-xs text-muted-foreground">Flapperless, Class 5 & Dual Flush Ready</p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="hidden sm:inline-flex text-xs">
                    Guaranteed Fit
                  </Badge>
                </div>
              </motion.div>
            </div>
          </div>

        </div>

        {/* Feature Grid Strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 mt-14 pt-8 border-t border-border">
          {highlights.map((item, index) => {
          const IconComponent = item.icon;
          return <Card data-index={index} key={index} className="bg-card/60 border border-border/80 shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                <CardContent className="p-5 flex items-start gap-4">
                  <div className="p-2.5 rounded-lg bg-secondary text-secondary-foreground shrink-0 mt-0.5">
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-card-foreground mb-1">
                      {item.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
                      {item.desc}
                    </p>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>

      </div>
    </section>;
}